import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Banknote, CalendarCheck, Globe, Shield, Terminal,
  Sparkles, ArrowRight, Server, KeyRound, BarChart3, Lock, GaugeCircle,
} from "lucide-react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import Head from "@/components/Head";
import ApiRequestDemo from "@/components/ApiRequestDemo";
import CodeBlock from "@/components/CodeBlock";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

const ICON_MAP = { banknote: Banknote, "calendar-check": CalendarCheck, globe: Globe, zap: Zap };

function useProducts() {
  const [p, setP] = React.useState([]);
  React.useEffect(() => {
    api.get("/api/products").then((r) => setP(r.data.items || []));
  }, []);
  return p;
}

const CODE_EXAMPLE = `# Fetch latest FX rates in 3 lines
curl -H "X-API-Key: $LOFTPRESS_KEY" \\
  "https://loftpress.shop/api/v1/currency/latest?base=USD"

# Add 5 business days in Germany
curl -H "X-API-Key: $LOFTPRESS_KEY" \\
  "https://loftpress.shop/api/v1/holidays/add-business-days?start_date=2026-03-24&days=5&country=DE"`;

const FAQ_KEYS = [
  { q: "landing.faq.q1", a: "landing.faq.a1" },
  { q: "landing.faq.q2", a: "landing.faq.a2" },
  { q: "landing.faq.q3", a: "landing.faq.a3" },
  { q: "landing.faq.q4", a: "landing.faq.a4" },
  { q: "landing.faq.q5", a: "landing.faq.a5" },
  { q: "landing.faq.q6", a: "landing.faq.a6" },
];

export default function LandingPage() {
  const { t, lang } = useLang();
  const products = useProducts();

  const seoTitle = lang === "tr"
    ? "LoftPress — API Platformu · Kur, Tatil ve IP API'ları"
    : "LoftPress — API Platform · Currency, Holiday & IP APIs";
  const seoDesc = lang === "tr"
    ? "LOFT PRESS LTD tarafından işletilen birinci taraf API platformu. Kur verisi, tatil takvimleri ve IP zekâsı; tek güvenli API gateway üzerinden, öngörülebilir fiyatlarla."
    : "First-party API platform operated by LOFT PRESS LTD. Currency FX, holiday calendars and IP intelligence delivered through one secure API gateway with predictable pricing.";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [1, 2, 3, 4, 5, 6].map((n) => ({
      "@type": "Question",
      "name": t(`landing.faq.q${n}`),
      "acceptedAnswer": { "@type": "Answer", "text": t(`landing.faq.a${n}`) },
    })),
  };
  const softwareJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "LoftPress API Platform",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any (REST API)",
    "provider": { "@type": "Organization", "name": "LOFT PRESS LTD", "url": "https://loftpress.shop" },
    "offers": [
      { "@type": "Offer", "name": "Pro", "price": "200", "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Enterprise", "price": "500", "priceCurrency": "USD" },
    ],
  };

  return (
    <div className="min-h-screen">
      <Head title={seoTitle} description={seoDesc} path="/" lang={lang} jsonLd={[faqJsonLd, softwareJsonLd]} />
      <Header />
      <Seo title={t("seo.landing.title")} description={t("seo.landing.desc")} path="/" jsonLd={{"@context":"https://schema.org","@type":"Organization","name":"LoftPress","url":"https://loftpress.shop","logo":"https://loftpress.shop/logo512.png","email":"support@loftpress.shop"}} />

      {/* Hero — text left, live terminal demo right (mobile: demo below) */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center" data-testid="hero-section">
        {/* Edge-to-edge atmosphere: subtle grid + off-centre gold radiance */}
        <div className="absolute inset-0 lp-grid-bg opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_78%)]" aria-hidden="true" />
        <div className="absolute top-1/2 left-[38%] -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[700px] rounded-full bg-[#d4af37]/6 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0b0e] to-transparent pointer-events-none" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-28 relative w-full">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            <div className="text-left">
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-medium text-white/95 leading-[1.02] tracking-tight text-5xl sm:text-6xl lg:text-7xl"
                data-testid="hero-title"
              >
                {t("hero.title.a")} <span className="lp-gold-text italic">{t("hero.title.c")}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="mt-7 max-w-xl text-lg sm:text-xl text-white/60 leading-relaxed"
              >
                {t("hero.subtitle")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-wrap items-center gap-3"
              >
                <Link to="/register" className="lp-btn-primary text-base px-6 py-3" data-testid="hero-cta-primary">
                  {t("hero.cta.primary")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/marketplace" className="lp-btn-secondary text-base px-6 py-3" data-testid="hero-cta-secondary">
                  {t("hero.cta.secondary")}
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full"
              data-testid="hero-demo-wrapper"
            >
              <ApiRequestDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="products-section">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.products.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white/95">{t("landing.products.title")}</h2>
          <p className="text-white/50 mt-2 max-w-2xl">{t("landing.products.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {products.length === 0 &&
            [1, 2, 3].map((i) => <div key={i} className="lp-card p-6 h-72 lp-shimmer" />)}
          {products.map((p) => {
            const Icon = ICON_MAP[p.icon] || Zap;
            return (
              <Link
                key={p.id}
                to={`/products/${p.slug}`}
                className="lp-card p-6 flex flex-col group"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#d4af37]/8 border border-[#d4af37]/25 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="lp-chip">{p.category}</span>
                    {p.coming_soon && (
                      <span className="lp-chip gold" data-testid={`landing-coming-soon-${p.slug}`}>
                        {t("marketplace.coming_soon")}
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-display text-xl text-white mt-5">{displayProductName((lang === "tr" && p.name_tr) || p.name)}</h3>
                <p className="text-white/50 text-sm mt-2 flex-1">{(lang === "tr" && p.tagline_tr) || p.tagline}</p>
                <div className="mt-5 flex items-center justify-between pt-5 border-t border-white/8">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">{t("landing.products.pro_from")}</div>
                    <div className="font-display text-lg text-white">
                      {p.coming_soon ? "—" : `$${p.plans_summary?.pro?.price?.toFixed(0) || "—"}`}
                      {!p.coming_soon && (
                        <span className="text-xs text-white/40 font-normal">{t("landing.pricing.per_month")}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-[#d4af37] group-hover:translate-x-1 transition-transform flex items-center gap-1">
                    {t("landing.products.explore")} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        <p className="text-xs text-white/40 mt-6 max-w-2xl">
          {t("landing.products.attribution")}
        </p>
      </section>

      {/* Pricing overview */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.pricing.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white/95">{t("landing.pricing.title")}</h2>
          <p className="text-white/50 mt-2">{t("landing.pricing.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {[
            { tier: "pro", name: t("landing.pricing.pro"), price: 200, reqs: "100,000", rate: "50 req/s", popular: true, cta: t("common.subscribe"), features: [t("landing.pricing.feat.pro.keys"), t("landing.pricing.feat.pro.rate"), t("landing.pricing.feat.pro.sla"), t("landing.pricing.feat.pro.support")] },
            { tier: "enterprise", name: t("landing.pricing.enterprise"), price: 500, reqs: "2,000,000", rate: "250 req/s", cta: t("common.subscribe"), features: [t("landing.pricing.feat.ent.keys"), t("landing.pricing.feat.ent.rate"), t("landing.pricing.feat.ent.support"), t("landing.pricing.feat.ent.audit")] },
          ].map((t2) => (
            <div key={t2.tier} className={`lp-card p-8 relative ${t2.popular ? "border-[#d4af37]/40" : ""}`}>
              {t2.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 lp-chip gold">{t("landing.pricing.popular")}</div>
              )}
              <div className="font-display text-lg text-white/90">{t2.name}</div>
              <div className="mt-4 flex items-end gap-1">
                <div className="font-display text-4xl text-white">
                  {t2.price === null ? t("landing.pricing.custom") : `$${t2.price}`}
                </div>
                {t2.price !== null && <span className="text-white/40 pb-1">{t("landing.pricing.per_month")}</span>}
              </div>
              {t2.price > 0 && (
                <div className="mt-1 text-xs text-white/45" data-testid={`plan-tax-line-${t2.tier}`}>
                  {t("pricing.tax.suffix")}
                </div>
              )}
              <div className="mt-2 text-sm text-white/60">
                {t2.reqs} {t("landing.pricing.reqs")} · {t2.rate}
              </div>
              <ul className="mt-6 space-y-3 text-sm text-white/70">
                {t2.features.map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#d4af37]" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/marketplace"
                className={`mt-8 ${t2.popular ? "lp-btn-primary" : "lp-btn-outline-gold"} w-full justify-center`}
                data-testid={`plan-cta-${t2.tier}`}
              >
                {t2.cta}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/45 mt-8 max-w-3xl mx-auto text-center" data-testid="pricing-tax-note">
          {t("pricing.tax.note")}
        </p>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.trust.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white/95">{t("landing.trust.title")}</h2>
          <p className="text-white/50 mt-2 max-w-2xl">{t("landing.trust.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Lock, title: t("landing.trust.hashed.title"), body: t("landing.trust.hashed.body") },
            { icon: Shield, title: t("landing.trust.ipallow.title"), body: t("landing.trust.ipallow.body") },
            { icon: KeyRound, title: t("landing.trust.audit.title"), body: t("landing.trust.audit.body") },
            { icon: BarChart3, title: t("landing.trust.spend.title"), body: t("landing.trust.spend.body") },
          ].map((it) => (
            <div key={it.title} className="lp-card p-6">
              <div className="w-10 h-10 rounded-lg bg-[#d4af37]/8 border border-[#d4af37]/25 flex items-center justify-center">
                <it.icon className="w-5 h-5 text-[#d4af37]" />
              </div>
              <h3 className="font-display text-lg text-white mt-4">{it.title}</h3>
              <p className="text-white/55 text-sm mt-2 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Developer-first */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.dev.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white/95">{t("landing.dev.title")}</h2>
            <p className="text-white/55 mt-4 max-w-lg">{t("landing.dev.subtitle")}</p>
            <ul className="mt-8 space-y-4">
              {[
                { icon: Terminal, title: t("landing.dev.one_url"), body: t("landing.dev.one_url_desc") },
                { icon: KeyRound, title: t("landing.dev.one_auth"), body: t("landing.dev.one_auth_desc") },
                { icon: BarChart3, title: t("landing.dev.one_env"), body: t("landing.dev.one_env_desc") },
              ].map((f) => (
                <li key={f.title} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <f.icon className="w-4 h-4 text-[#d4af37]" />
                  </div>
                  <div>
                    <div className="text-white font-medium">{f.title}</div>
                    <div className="text-white/50 text-sm">{f.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <CodeBlock code={CODE_EXAMPLE} language="bash" testId="dev-code-example" />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-24" data-testid="faq-section">
        <div className="mb-10 text-center">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-2">{t("landing.faq.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white/95">{t("landing.faq.title")}</h2>
        </div>
        <div className="space-y-3">
          {FAQ_KEYS.map((f, i) => <FAQItem key={i} q={t(f.q)} a={t(f.a)} idx={i} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = React.useState(idx === 0);
  return (
    <div className="lp-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
        data-testid={`faq-item-${idx}`}
      >
        <span className="font-medium text-white/90">{q}</span>
        <span className={`text-[#d4af37] transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.28 }}
        className="overflow-hidden"
      >
        <p className="px-6 pb-5 text-white/60 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}
